import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { MonitoredCallDetail, RemoteProvenance } from "@shared/types";
import { MONITOR_ENVIRONMENT_LABELS, REMOTE_STATUS_LABELS } from "@shared/types";
import { ApiError } from "../../lib/api";
import { formatDuration, getSourceCall, prettyPhone, relativeTime, shortRevision } from "../../lib/monitoring";
import { Card } from "../../components/ui/card";
import { EnvBadge, InternalTestBadge, OriginBadge, ResultBadge } from "../../components/supervision/badges";

function stamp(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/**
 * Une conversation : qui, quand, ce que l'assistant a compris, avec quel
 * moteur et quelle version, puis la transcription complète. L'environnement
 * reste affiché en tête : on ne lit jamais un test en croyant lire un client.
 */
export function SupervisionCallPage() {
  const { sourceId, callId } = useParams();
  const [detail, setDetail] = useState<MonitoredCallDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!sourceId || !callId) return;
    let cancelled = false;
    getSourceCall(sourceId, callId)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(
          err instanceof ApiError && err.status === 404
            ? "Cette conversation n'existe plus chez le client."
            : "La conversation n'a pas pu être lue depuis le portail du client.",
        );
      });
    return () => {
      cancelled = true;
    };
  }, [sourceId, callId]);

  const back = `/supervision?source=${encodeURIComponent(sourceId ?? "")}`;

  if (error) {
    return (
      <div className="supervision">
        <Link to={back} className="btn btn--ghost btn--sm">
          ← Retour aux conversations
        </Link>
        <p className="sv-error" role="alert">
          {error}
        </p>
      </div>
    );
  }
  if (!detail) return <div className="supervision__empty">Chargement…</div>;

  const { source, call, remote } = detail;
  const env = source.environment;
  const assistant = "Assistant";

  return (
    <div className={`supervision supervision--${env} sv-call`}>
      <div>
        <Link to={back} className="btn btn--ghost btn--sm">
          ← Retour aux conversations
        </Link>
      </div>

      <div className={`env-strip env-strip--${env}`} role="status">
        <EnvBadge environment={env} />
        <span className="env-strip__who">
          {source.clientName}
          {source.phoneNumber ? <span className="env-strip__phone"> · ligne {prettyPhone(source.phoneNumber)}</span> : null}
        </span>
        <span className="env-strip__hint">
          {env === "preprod" ? "Conversation d'essai." : "Conversation d'un client réel."}
        </span>
      </div>

      <header className="sv-call__head">
        <div className="sv-call__titles">
          <p className="sv-call__eyebrow">
            {new Date(call.startedAt).toLocaleString("fr-CA", { dateStyle: "full", timeStyle: "short" })} · {relativeTime(call.startedAt)}
          </p>
          <h1 className="sv-call__title">
            {call.callerName ? `${call.callerName} · ` : ""}
            <span className="sv-call__phone">{prettyPhone(call.fromNumber)}</span>
          </h1>
          <p className="sv-call__facts">
            {call.status === "en_cours" ? "En cours" : `Durée ${formatDuration(call.durationSec)}`}
            {call.toNumber ? ` · reçu au ${prettyPhone(call.toNumber)}` : ""}
            {call.status !== "en_cours" && call.status !== "termine" ? ` · ${REMOTE_STATUS_LABELS[call.status]}` : ""}
          </p>
        </div>
        <div className="sv-call__badges">
          <ResultBadge status={call.status} outcome={call.outcome} />
          <OriginBadge origin={call.origin} />
          {call.internalTest ? <InternalTestBadge /> : null}
        </div>
      </header>

      <div className="sv-call__grid">
        <Card title="Ce que l'assistant a compris" subtitle="Champs remplis par la brique vocale pendant l'appel.">
          <dl className="sv-kv">
            <dt>Intention</dt>
            <dd>{call.intent ?? "—"}</dd>
            <dt>Véhicule</dt>
            <dd>{call.vehicle ?? "—"}</dd>
            <dt>Service</dt>
            <dd>{call.service ?? "—"}</dd>
            <dt>Créneau demandé</dt>
            <dd>{call.requestedAt ?? "—"}</dd>
            <dt>Rendez-vous</dt>
            <dd>{call.appointmentId ? `créé (${call.appointmentId})` : "aucun"}</dd>
          </dl>
          {call.summary ? <p className="sv-call__summary">{call.summary}</p> : null}
        </Card>

        <Card title="Moteur et version" subtitle="Ce que la brique vocale a déclaré pour cet appel.">
          <Provenance provenance={call.provenance} />
        </Card>

        <Card title="Suivi par l'équipe du client" subtitle="Lecture seule : les notes se modifient dans le portail du client.">
          <dl className="sv-kv">
            <dt>Traité</dt>
            <dd>{call.handled ? "Oui" : "Non"}</dd>
            <dt>Notes</dt>
            <dd>{call.notes ? <span className="sv-call__notes">{call.notes}</span> : "—"}</dd>
            <dt>Identifiant</dt>
            <dd>
              <code className="sv-code">{call.providerId ?? call.id}</code>
            </dd>
          </dl>
        </Card>
      </div>

      {call.recordingUrl ? (
        <Card title="Enregistrement">
          <audio controls src={call.recordingUrl} className="sv-audio" />
        </Card>
      ) : null}

      <Card title="Transcription" subtitle={`${call.transcript.length} réplique${call.transcript.length > 1 ? "s" : ""}`}>
        {call.transcript.length === 0 ? (
          <p className="sv-dim">Aucune transcription transmise.</p>
        ) : (
          <ol className="sv-transcript">
            {call.transcript.map((l, i) => (
              <li key={`${l.at}-${i}`} className={`sv-transcript__line sv-transcript__line--${l.speaker}`}>
                <span className="sv-transcript__meta">
                  <span className="sv-transcript__who">{l.speaker === "ia" ? assistant : "Appelant"}</span>
                  <span className="sv-transcript__at">{stamp(l.at)}</span>
                </span>
                <span className="sv-transcript__bubble">{l.text}</span>
              </li>
            ))}
          </ol>
        )}
      </Card>

      <p className="sv-foot">
        Lu depuis {source.baseUrl} ({remote.client}, {MONITOR_ENVIRONMENT_LABELS[remote.environment].toLowerCase()}) le{" "}
        {new Date(remote.generatedAt).toLocaleString("fr-CA")}.
      </p>
    </div>
  );
}

function Provenance({ provenance: p }: { provenance: RemoteProvenance | null }) {
  if (!p) {
    return (
      <p className="sv-dim">
        Provenance non transmise : appel enregistré avant que la brique vocale ne la pousse, ou instance pas
        encore à jour.
      </p>
    );
  }
  const engine = p.engine ?? "inconnu";
  // Le relais historique n'écrit pas de diagnostic par tour : ses compteurs à zéro ne sont pas une mesure.
  const counted = (p.preparedTurns ?? 0) > 0 || (p.modelCalls ?? 0) > 0 || engine === "blueprint";
  return (
    <dl className="sv-kv">
      <dt>Moteur</dt>
      <dd>
        <span className={`sv-engine__name sv-engine__name--${engine}`}>{engine}</span>
        {p.profile ? <span className="sv-dim"> · profil {p.profile}</span> : null}
      </dd>
      <dt>Application</dt>
      <dd>
        <code className="sv-code" title={p.appRevision ?? undefined}>
          {shortRevision(p.appRevision)}
        </code>
      </dd>
      <dt>Moteur commun</dt>
      <dd>
        <code className="sv-code" title={p.engineRevision ?? undefined}>
          {shortRevision(p.engineRevision)}
        </code>
      </dd>
      <dt>Modèles</dt>
      <dd>
        {p.fastModel ?? "—"}
        {p.smartModel ? <span className="sv-dim"> · {p.smartModel}</span> : null}
      </dd>
      <dt>Voix</dt>
      <dd>
        <code className="sv-code">{p.voice ?? "—"}</code>
      </dd>
      <dt>Tours sans modèle</dt>
      <dd>{counted ? (p.preparedTurns ?? "—") : <span className="sv-dim">non comptés sur ce relais</span>}</dd>
      <dt>Requêtes modèle</dt>
      <dd>
        {counted ? (p.modelCalls ?? "—") : <span className="sv-dim">non comptées sur ce relais</span>}
        {counted && p.modelMs ? <span className="sv-dim"> · {(p.modelMs / 1000).toFixed(1)} s cumulées côté serveur</span> : null}
      </dd>
    </dl>
  );
}
